import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import { debounce } from 'essential-utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlexBox } from '../box';
import { styled } from '../styled';
import { usePaletteColor } from '../theme';
import { Tooltip } from '../tooltip';
import { Chip } from '.';
const MIN_CHIP_WIDTH = 80;
const FadeOut = styled('div')(({ theme }) => ({
    position: 'absolute',
    width: theme.spacing(4),
    height: '100%',
    pointerEvents: 'none',
    maskImage: `linear-gradient(to left, white ${theme.spacing(1)}, rgba(255, 255, 255, 0) 100%)`
}));
const BackBox = styled('div')({
    position: 'absolute',
    right: 0,
    height: '100%',
    backgroundColor: 'white',
    zIndex: 1
});
export const ChipGroup = ({ children = [], placement = 'auto', onVisibilityChange, ...flexBoxProps }) => {
    const { spacing } = useTheme();
    const [containerWidth, setContainerWidth] = useState(0);
    const [node, setNode] = useState();
    const [plusChipWidth, setPlusChipWidth] = useState(0);
    const backgroundColor = usePaletteColor(flexBoxProps.bgcolor ?? 'background.paper');
    const chipRefs = useRef([]);
    const GAP_UNIT = 1;
    const GAP = parseInt(spacing(GAP_UNIT), 10);
    const [hiddenChips, setHiddenChips] = useState([]);
    const calculate = useCallback(() => {
        if (!node) {
            return;
        }
        const containerWidth = node.offsetWidth || 0;
        setContainerWidth(containerWidth);
        const widths = chipRefs.current.map((ref) => ref?.offsetWidth || 0);
        const { hidden } = widths.reduce((acc, width, index) => {
            if (acc.collectedWidth + width <= containerWidth) {
                acc.collectedWidth += width + GAP;
                return acc;
            }
            if (acc.collectedWidth + MIN_CHIP_WIDTH < containerWidth) {
                acc.collectedWidth += MIN_CHIP_WIDTH;
            }
            acc.hidden.push(children[index]);
            return acc;
        }, {
            hidden: [],
            collectedWidth: 0
        });
        setHiddenChips(hidden);
    }, [children, node, GAP]);
    useEffect(() => {
        if (!node) {
            return;
        }
        const debouncedCalculate = debounce(calculate, 25);
        const observer = new ResizeObserver(() => {
            debouncedCalculate();
        });
        observer.observe(node);
        return () => {
            debouncedCalculate.cancel();
            observer?.disconnect();
            setNode(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node]);
    const refCallback = React.useCallback((node) => {
        if (!node) {
            return;
        }
        setNode(node);
    }, []);
    useEffect(() => {
        calculate();
    }, [calculate, children]);
    const refPlusChipCallback = React.useCallback((node) => {
        if (!node) {
            return;
        }
        setPlusChipWidth(node.offsetWidth);
    }, []);
    const Chips = () => {
        if (!children.length) {
            return null;
        }
        if (children.length === 1) {
            const chip = React.cloneElement(children[0], {
                maxWidth: containerWidth - 2 * GAP
            });
            return (_jsx(FlexBox, { alignItems: "center", height: "100%", children: chip }));
        }
        return (_jsxs(FlexBox, { overflow: "hidden", gap: GAP_UNIT, alignItems: "center", height: "100%", maxWidth: containerWidth, children: [_jsx(FlexBox, { display: "flex", gap: GAP_UNIT, alignItems: "center", children: children.map((child, index) => (_jsx(FlexBox, { ref: (el) => (chipRefs.current[index] = el), alignItems: "center", justifyContent: "center", children: child }, `chip-${index}`))) }), Boolean(hiddenChips.length) && (_jsxs(FlexBox, { position: "absolute", right: 0, children: [Boolean(plusChipWidth) && (_jsxs(_Fragment, { children: [_jsx(FadeOut, { "data-role": "chip-group-fade-out", sx: {
                                        backgroundColor,
                                        right: `${plusChipWidth - 1}px`
                                    } }), _jsx(BackBox, { sx: {
                                        width: `${plusChipWidth}px`
                                    } })] })), _jsx(Tooltip, { onVisibilityChange: onVisibilityChange, placement: placement, title: _jsx(FlexBox, { gap: GAP_UNIT, flexWrap: "wrap", p: 1, children: hiddenChips.map((child, index) => (_jsx(FlexBox, { alignItems: "center", justifyContent: "center", children: child }, `hidden-${index}`))) }), mode: "light", children: _jsx(Chip, { ref: refPlusChipCallback, label: `+${hiddenChips.length}`, sx: {
                                    zIndex: 2
                                } }) })] }))] }));
    };
    return (_jsx(FlexBox, { "data-role": "chip-group", position: "relative", width: "100%", alignItems: "center", height: "100%", ...flexBoxProps, ref: refCallback, children: Boolean(containerWidth) && _jsx(Chips, {}) }));
};
